import { locales } from './locales'

/**
 * Surfaces nexus serves at a **single, locale-free** path. Each is a whole first
 * segment: `/blog/…` is canonical unprefixed and `/${locale}/blog/…` 301s to it,
 * and `/social` lives outside the `[locale]` tree entirely (both in `src/proxy.ts`).
 */
const LOCALE_FREE_SEGMENTS = new Set(['blog', 'social'])

/**
 * The href's first path segment, or `''` when there is none.
 *
 * Query and hash are stripped first: `/blog?mood=x` would otherwise segment to
 * `blog?mood=x`, miss the locale-free match, and be prefixed into a `301`.
 */
const firstSegment = (pathname: string): string =>
    pathname
        .split(/[?#]/)[0]
        .split('/')
        .find((segment) => segment !== '') ?? ''

const pathnameOf = (href: unknown): string | null => {
    if (typeof href === 'string') {
        return href
    }
    if (typeof href === 'object' && href !== null) {
        const { pathname } = href as { pathname?: unknown }
        return typeof pathname === 'string' ? pathname : null
    }
    return null
}

/**
 * Whether the locale-aware `Link` should leave this href alone instead of
 * prepending the active locale. Two independent reasons, both ending in a URL
 * that would redirect (`invariants.md#rendered-links-are-canonical`):
 *
 * - **already prefixed** — `/en-us/foo/` would become `/en-us/en-us/foo/`. This is
 *   what lets a caller resolve a destination early — on the server, or from data
 *   that already carries the locale — and still route it through `Link`.
 * - **a locale-free surface** — `/blog/…`, `/social`. One canonical path, no
 *   per-locale variant.
 *
 * **This is a property of the destination, not of the page asking.** The two come
 * apart in both directions: a component on a locale-free `/blog/…` page can link to
 * a route that *must* be prefixed, and a component under `/en-us/` can link to a
 * `/blog/…` post that must *not* be. So no amount of knowing where the current
 * render sits — `next/root-params`, the active locale — can answer this; only the
 * href can. (`next/root-params` is also a compiler-replaced placeholder that throws
 * outside a Next build, so it is unavailable here anyway.)
 *
 * Matching is whole-segment on purpose: `/blogging-tips/` is an ordinary route that
 * merely shares a prefix with `/blog`, and `/en-us-guide/` is not a locale.
 *
 * Lives in its own leaf module — no React, no client/server imports — so the
 * server-safe and `'use client'` navigation modules can both depend on it, the same
 * arrangement `isDefaultLocaleHomepage` uses. `href` is `unknown` to cover the
 * string and `UrlObject` shapes next-intl accepts.
 */
export const needsNoLocalePrefix = (href: unknown): boolean => {
    const pathname = pathnameOf(href)
    // A protocol-relative URL also "starts with /" — `//evil.test/x` would segment
    // to `evil.test` and, if that ever looked locale-free, render verbatim as an
    // off-host destination. Only a single leading slash is a path.
    if (!pathname?.startsWith('/') || pathname.startsWith('//')) {
        return false
    }

    const segment = firstSegment(pathname)
    return LOCALE_FREE_SEGMENTS.has(segment) || (locales as readonly string[]).includes(segment)
}
